import * as React from 'react';
import { Paperclip, Upload, X } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from './button';

export type FileRejectionReason =
  'invalid-type' | 'too-large' | 'too-many' | 'duplicate';

export interface FileRejection {
  file: File;
  reason: FileRejectionReason;
}

export interface FileDropzoneLabels {
  /**
   * Primary line inside the zone. Callers bake their own constraint text
   * ("5 files max, 10 MB each") in here — the design system has no i18n and
   * cannot pluralize or format bytes.
   */
  hint: string;
  /** Browse button text. This button is the zone's only tab stop. */
  browse: string;
  /** aria-label of a chip's remove button. */
  removeFile: string;
  /** Optional line between the hint and the button. Omit to hide it. */
  or?: string;
  /** Chip size suffix. @default `${Math.round(bytes / 1024)} KB` */
  fileSize?: (bytes: number) => string;
}

interface FileDropzoneBaseProps {
  labels: FileDropzoneLabels;
  /**
   * Native `accept` syntax: `".csv,.xlsx"`, `"image/png,application/pdf"`,
   * `"image/*"`. Drives BOTH the OS picker filter and drop/paste validation, so
   * the two cannot drift. A file passes if it matches ANY token:
   * `.ext` → case-insensitive suffix match on `file.name`; `type/*` → prefix
   * match on `file.type`; `type/sub` → case-insensitive exact `file.type`.
   * Omitted → everything passes.
   */
  accept?: string;
  /** Per-file byte limit. Violations surface as `'too-large'`. */
  maxSize?: number;
  /**
   * Binds a `window` paste listener while mounted and enabled. Only one
   * `pasteEnabled` dropzone may be mounted at a time — two would both consume
   * the same paste.
   * @default false
   */
  pasteEnabled?: boolean;
  /** Called once per drop/paste/pick with every rejection in that batch. */
  onReject?: (rejections: FileRejection[]) => void;
  /**
   * `compact` = in-dialog (`p-8`, bare icon, small button).
   * `page` = full-page step (`px-6 py-16`, icon in a circle, default button).
   * @default "compact"
   */
  variant?: 'compact' | 'page';
  /** @default <Upload /> */
  icon?: React.ReactNode;
  /** Slot rendered inside each chip — upload spinner, percentage, check… */
  fileAdornment?: (file: File, index: number) => React.ReactNode;
  disabled?: boolean;
  /** Lands on the root container. Not on the Browse button: a `<Label
   *  htmlFor>` pointing at a button overrides its accessible name, hiding
   *  the browse label from screen readers and role-based queries. */
  id?: string;
  className?: string;
}

type FileDropzoneValueProps =
  | {
      multiple?: false;
      value: File | null;
      onChange: (file: File | null) => void;
      /** Meaningless without `multiple` — `never` so the union discriminates. */
      maxFiles?: never;
    }
  | {
      multiple: true;
      value: File[];
      onChange: (files: File[]) => void;
      /** Omit for unlimited. Overflow surfaces as `'too-many'`. */
      maxFiles?: number;
    };

export type FileDropzoneProps = FileDropzoneBaseProps & FileDropzoneValueProps;

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith('.')) return name.endsWith(token);
      if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
      return type === token;
    });
}

/** Identity for the duplicate check — `File` has no stable id. */
function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * The validation pipeline, exported for callers that must run it outside the
 * component (e.g. a paste listener with a wider scope than the zone's mount).
 * Checks run type → size → duplicate → capacity, so a wrong-type file never
 * consumes one of the `maxFiles` slots.
 */
export function pickAcceptedFiles(
  incoming: File[],
  options: {
    accept?: string;
    maxSize?: number;
    maxFiles?: number;
    /** Already-selected files — used for the duplicate and capacity checks. */
    current?: File[];
  },
): { accepted: File[]; rejected: FileRejection[] } {
  const { accept, maxSize, maxFiles, current = [] } = options;
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];
  const seen = new Set(current.map(fileKey));

  for (const file of incoming) {
    if (!matchesAccept(file, accept)) {
      rejected.push({ file, reason: 'invalid-type' });
      continue;
    }
    if (maxSize !== undefined && file.size > maxSize) {
      rejected.push({ file, reason: 'too-large' });
      continue;
    }
    const key = fileKey(file);
    if (seen.has(key)) {
      rejected.push({ file, reason: 'duplicate' });
      continue;
    }
    if (
      maxFiles !== undefined &&
      current.length + accepted.length >= maxFiles
    ) {
      rejected.push({ file, reason: 'too-many' });
      continue;
    }
    seen.add(key);
    accepted.push(file);
  }

  return { accepted, rejected };
}

function defaultFileSize(bytes: number): string {
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Drag-and-drop + browse + optional paste zone. Replaces the native file
 * input's visible surface, whose "Choose file" chrome is drawn from the
 * browser's locale and cannot be translated.
 */
export function FileDropzone(props: FileDropzoneProps) {
  const {
    labels,
    accept,
    maxSize,
    pasteEnabled = false,
    onReject,
    variant = 'compact',
    icon,
    fileAdornment,
    disabled = false,
    id,
    className,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  // A depth counter, not a boolean: dragleave fires whenever the cursor crosses
  // onto an inner child, which would make a boolean flicker.
  const dragDepth = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);

  const selected = props.multiple
    ? props.value
    : props.value
      ? [props.value]
      : [];

  const acceptFiles = (incoming: File[]) => {
    if (disabled || incoming.length === 0) return;
    const { accepted, rejected } = pickAcceptedFiles(incoming, {
      accept,
      maxSize,
      // Single mode has replace semantics: it takes the first match and
      // silently ignores the rest, so 'too-many' and 'duplicate' never apply.
      maxFiles: props.multiple ? props.maxFiles : undefined,
      current: props.multiple ? props.value : [],
    });
    if (rejected.length > 0) onReject?.(rejected);
    if (accepted.length === 0) return;
    if (props.multiple) props.onChange([...props.value, ...accepted]);
    else props.onChange(accepted[0]);
  };

  // Kept fresh through a ref so the paste listener never closes over stale props.
  const acceptFilesRef = React.useRef(acceptFiles);
  React.useEffect(() => {
    acceptFilesRef.current = acceptFiles;
  });

  React.useEffect(() => {
    if (!pasteEnabled) return;
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      // Let a plain-text paste fall through to whatever is focused.
      if (pasted.length === 0) return;
      event.preventDefault();
      acceptFilesRef.current(pasted);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [pasteEnabled]);

  const removeAt = (index: number) => {
    if (props.multiple)
      props.onChange(props.value.filter((_, i) => i !== index));
    else props.onChange(null);
  };

  const isPage = variant === 'page';
  const iconNode = icon ?? <Upload />;

  return (
    <div id={id} className={cn('space-y-2', className)}>
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          if (disabled) return;
          dragDepth.current += 1;
          setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (disabled) return;
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setIsDragging(false);
          acceptFiles(Array.from(event.dataTransfer.files));
        }}
        className={cn(
          'flex flex-col items-center justify-center rounded-xl border-2 border-dashed bg-muted/30 text-center transition-colors focus-within:border-riso-blue',
          isPage ? 'gap-4 px-6 py-16' : 'gap-2 p-8',
          isDragging ? 'border-riso-blue bg-riso-blue/5' : 'border-border',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {isPage ? (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted [&>svg]:h-6 [&>svg]:w-6 [&>svg]:text-muted-foreground">
            {iconNode}
          </div>
        ) : (
          <div className="[&>svg]:h-8 [&>svg]:w-8 [&>svg]:text-muted-foreground">
            {iconNode}
          </div>
        )}

        <p
          className={cn(
            isPage ? 'font-medium' : 'text-sm text-muted-foreground',
          )}
        >
          {labels.hint}
        </p>
        {labels.or && (
          <p className="text-sm text-muted-foreground">{labels.or}</p>
        )}

        <Button
          type="button"
          variant="outline"
          size={isPage ? 'default' : 'sm'}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          {labels.browse}
        </Button>

        {/* Kept display:none, not sr-only: an sr-only input stays focusable and
            would re-announce the untranslatable native "Choose file" chrome. */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={props.multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden
          className="hidden"
          onChange={(event) => {
            acceptFiles(Array.from(event.target.files ?? []));
            // Without this, re-picking the same file after removing it is a no-op.
            event.target.value = '';
          }}
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2" aria-live="polite">
          {selected.map((file, index) => (
            <span
              key={`${fileKey(file)}-${index}`}
              className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs shadow-hard-sm"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="max-w-[16rem] truncate font-medium">
                {file.name}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {(labels.fileSize ?? defaultFileSize)(file.size)}
              </span>
              {fileAdornment?.(file, index)}
              <button
                type="button"
                aria-label={labels.removeFile}
                disabled={disabled}
                onClick={() => removeAt(index)}
                className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
